import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import { Loader2 } from "lucide-react";
import "./App.css";

// Critical pages - loaded immediately for fast invoice workflow
import Dashboard from "./pages/Dashboard";
import InvoiceEditor from "./pages/InvoiceEditor";
import InvoiceHistory from "./pages/InvoiceHistory";
import Customers from "./pages/Customers";

// LITE MODE - Standalone fast invoice creator (no layout overhead)
import LiteInvoice from "./pages/LiteInvoice";

// PRINT MODE - Clean print-optimized invoice view
import PrintInvoice from "./pages/PrintInvoice";

// Non-critical pages - lazy loaded to improve cold start performance
const Summary = lazy(() => import("./pages/Summary"));
const Vehicles = lazy(() => import("./pages/Vehicles"));
const Settings = lazy(() => import("./pages/Settings"));

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
  </div>
);

// PWA components - lazy loaded as they're not critical for core functionality
const PWAComponents = lazy(() => import("./components/PWAComponents").then(module => ({
  default: () => (
    <>
      <module.PWAInstallPrompt />
      <module.OfflineIndicator />
    </>
  )
})));

function App() {
  return (
    <div className="App">
      <Suspense fallback={null}>
        <PWAComponents />
      </Suspense>
      <BrowserRouter>
        <Routes>
          {/* LITE MODE - Standalone route without main app layout */}
          <Route path="/lite" element={<LiteInvoice />} />
          <Route path="/quick" element={<LiteInvoice />} />
          
          {/* PRINT MODE - Clean print view, opens in new tab */}
          <Route path="/print/:id" element={<PrintInvoice />} />
          
          {/* FULL APP - With sidebar layout */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="customers" element={<Customers />} />
            <Route path="vehicles" element={
              <Suspense fallback={<PageLoader />}>
                <Vehicles />
              </Suspense>
            } />
            <Route path="invoices" element={<InvoiceHistory />} />
            <Route path="invoices/new" element={<InvoiceEditor />} />
            <Route path="invoices/:id" element={<InvoiceEditor />} />
            <Route path="summary" element={
              <Suspense fallback={<PageLoader />}>
                <Summary />
              </Suspense>
            } />
            <Route path="settings" element={
              <Suspense fallback={<PageLoader />}>
                <Settings />
              </Suspense>
            } />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
